import { ColumnDef } from "@tanstack/react-table";
import { FlightPlanRequest } from "@/api/v1/flight-plan-types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar } from "lucide-react";
import { format } from "date-fns";
import { Link } from "@tanstack/react-router";

const getStatusColor = (status: string) => {
  switch (status) {
    case "approved":
      return "bg-green-500/10 text-green-500 hover:bg-green-500/20";
    case "rejected":
      return "bg-red-500/10 text-red-500 hover:bg-red-500/20";
    case "submitted":
    case "awaiting_approval":
    case "validating":
      return "bg-blue-500/10 text-blue-500 hover:bg-blue-500/20";
    case "no_conflicts_found":
      return "bg-slate-500/10 text-slate-500 hover:bg-slate-500/20";
    case "requires_external_authorisation":
    case "pending_external_authorisation":
      return "bg-amber-500/10 text-amber-500 hover:bg-amber-500/20";
    case "draft":
    case "cancelled":
    case "expired":
      return "bg-gray-500/10 text-gray-500 hover:bg-gray-500/20";
    default:
      return "bg-gray-500/10 text-gray-500 hover:bg-gray-500/20";
  }
};

const getStatusDisplay = (status: string) => {
  if (status === "pending_external_authorisation" || status === "requires_external_authorisation") {
    return "pending external authorisation";
  }
  return status.replace(/_/g, " ");
};

export const columns: ColumnDef<FlightPlanRequest>[] = [
  {
    accessorKey: "reference_number",
    header: "Reference",
    cell: ({ row }) => {
      const ref = row.getValue("reference_number") as string;
      const id = row.original.id;
      return <Link to={`/flight-plans/$flightPlanId`} params={{ flightPlanId: id }} className="hover:underline">{ref || id}</Link>;
    },
  },
  {
    accessorKey: "purpose",
    header: "Purpose",
  },
  {
    accessorKey: "status",
    header: "Status",
    filterFn: (row, id, value) => {
      return value === "all" || row.getValue(id) === value;
    },
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      return (
        <Badge variant="secondary" className={getStatusColor(status)}>
          {getStatusDisplay(status)}
        </Badge>
      );
    },
  },
  {
    accessorKey: "starts_at",
    header: "Schedule",
    cell: ({ row }) => {
      const start = new Date(row.getValue("starts_at"));
      const end = new Date(row.original.ends_at);
      return (
        <div className="flex flex-col text-sm">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Calendar className="h-3 w-3" />
            {format(start, "MMM d, yyyy")}
          </div>
          <span className="text-xs">
            {format(start, "HH:mm")} - {format(end, "HH:mm")}
          </span>
        </div>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      return (
        <div className="text-right">
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/flight-plans/$flightPlanId`} params={{ flightPlanId: row.original.id }}>
              View
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
        </div>
      );
    },
  },
];
